<section class="admin-users">
  <app-page-header
    title="Gestión de Usuarios"
    [subtitle]="pageSubtitle()"
    icon="User"
  />

  @if (!canManage()) {
    <div class="admin-users__denied card-like">
      <p class="admin-users__denied-title">Acceso restringido</p>
      <p class="admin-users__denied-text">
        No tienes permisos para administrar usuarios. Se requiere rol de administrador de empresa o
        superadministrador.
      </p>
    </div>
  } @else {
    @if (error() && !formOpen()) {
      <app-vault-page-alert
        type="error"
        title="Error"
        [message]="error()"
        (closed)="error.set(null)"
      />
    }
    @if (successFeedback() && !formOpen()) {
      <app-vault-page-alert
        type="success"
        [title]="successFeedback()!.title"
        [message]="successFeedback()!.message"
        [duration]="successAlertDurationMs"
        (closed)="dismissSuccessFeedback()"
      />
    }

    @if (isSuperAdmin() && domainRestricted() && allowedDomains().length) {
      <p class="admin-users__domains-hint">
        Dominios de email permitidos: {{ allowedDomains().join(', ') }}
      </p>
    }

    <div class="admin-users__toolbar">
      <lib-button variant="primary" type="button" prefixIcon="Plus" [disabled]="loading()" (clicked)="openCreate()">
        Nuevo usuario
      </lib-button>
      <lib-button variant="secondary" type="button" prefixIcon="RefreshCcw" [loading]="loading()" (clicked)="reload()">
        Actualizar
      </lib-button>
    </div>

    <section class="admin-users__stats" aria-label="Resumen">
      <article class="admin-users__stat">
        <h3>Total</h3>
        <p>{{ stats().total }}</p>
      </article>
      <article class="admin-users__stat">
        <h3>Activos</h3>
        <p class="is-ok">{{ stats().active }}</p>
      </article>
      <article class="admin-users__stat">
        <h3>Inactivos</h3>
        <p>{{ stats().inactive }}</p>
      </article>
      @if (isSuperAdmin()) {
        <article class="admin-users__stat">
          <h3>Superadmins</h3>
          <p class="is-warn">{{ stats().superadmins }}</p>
        </article>
      }
    </section>

    @if (loading()) {
      <div class="admin-users__skeleton" aria-busy="true">
        <lib-skeleton variant="rectangle" [height]="'48px'" />
        <lib-skeleton variant="rectangle" [height]="'48px'" />
        <lib-skeleton variant="rectangle" [height]="'48px'" />
      </div>
    } @else if (users().length === 0) {
      <lib-empty icon="Users" message="Sin usuarios" description="No hay usuarios que mostrar o el listado está vacío." />
    } @else {
      <div class="admin-users__table-wrap">
        <lib-search
          placeholder="Buscar por email o usuario..."
          [searchTerm]="searchTerm()"
          (searchTermChange)="onTableSearchChange($event)"
          [loading]="loading()"
        />
        @if (tableData().length === 0) {
          <lib-empty
            icon="Search"
            message="Sin resultados"
            description="No hay usuarios que coincidan con la búsqueda."
          />
        } @else {
          <lib-table
            [config]="tableConfig()"
            [data]="tableData()"
            [loading]="loading()"
            [searchTerm]="searchTerm()"
            (searchTermChange)="onTableSearchChange($event)"
            [pageSize]="tablePageSize()"
            [pageSizeOptions]="tablePageSizeOptions"
            (pageChange)="onTablePageChange($event)"
            [sortColumn]="tableSortColumn()"
            [sortDirection]="tableSortDirection()"
            (sortChange)="onTableSort($event)"
            (action)="onTableAction($event)"
          >
            <ng-template #cellTemplate let-row let-column="column">
              @if (column.key === 'statusLabel') {
                <lib-badge
                  [text]="row.statusLabel"
                  [variant]="row.is_active ? 'success' : 'neutral'"
                  size="small"
                />
              }
            </ng-template>
          </lib-table>
        }
      </div>
    }
  }
</section>

@if (formOpen()) {
  <div class="admin-users__backdrop" (click)="closeForm()" role="presentation"></div>
  <div
    class="admin-users__dialog"
    role="dialog"
    aria-modal="true"
    aria-labelledby="admin-user-form-title"
    autocomplete="off"
    data-lpignore="true"
    data-1p-ignore="true"
    data-bwignore="true"
  >
    @if (error()) {
      <div class="admin-users__dialog-feedback" role="alert">
        <lib-alert
          type="error"
          title="Error"
          [message]="error()!"
          [closeButton]="true"
          (closed)="error.set(null)"
        />
      </div>
    }
    <header class="admin-users__dialog-head">
      <h2 id="admin-user-form-title">
        @if (formMode() === 'create') {
          {{ formCreateStep() === 1 ? 'Nuevo usuario' : 'Asignar clave de acceso' }}
        } @else {
          Editar usuario
        }
      </h2>
      <p class="admin-users__form-intro">{{ formIntroText() }}</p>
    </header>
    <div class="admin-users__dialog-body" (click)="$event.stopPropagation()">
      @if (formMode() !== 'create' || formCreateStep() === 1) {
      <form
        class="admin-users__form-shell"
        autocomplete="off"
        novalidate
        (submit)="$event.preventDefault()"
      >
        <app-grid-form preset="2cols" [columns]="2" gap="1rem" wrapperClass="admin-users__form">
        <app-grid-form-item [span]="1">
          <app-field-label
            label="Nombre para mostrar"
            [required]="true"
            [tooltip]="fullNameFieldTooltip()"
          />
          <lib-input
            label=""
            placeholder="Nombre y apellidos"
            vaultInputAutocomplete="off"
            vaultInputReadonlyUntilFocus
            vaultInputIgnorePasswordManagers
            [vaultInputName]="'vault-new-display-' + formAutofillNonce()"
            [value]="formFullName()"
            (valueChange)="formFullName.set($event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="1">
          <app-field-label label="Correo electrónico" [required]="true" [tooltip]="emailFieldTooltip()" />
          <lib-input
            label=""
            type="text"
            vaultInputAutocomplete="off"
            vaultInputReadonlyUntilFocus
            vaultInputIgnorePasswordManagers
            [vaultInputName]="'vault-new-email-' + formAutofillNonce()"
            placeholder="usuario@dominio.com"
            [errorMsg]="formEmailError()"
            [value]="formEmail()"
            (valueChange)="onFormEmailChange($event)"
          />
          @if (isSuperAdmin() && domainRestricted() && allowedDomains().length) {
            <p class="admin-users__hint">
              Dominios permitidos: {{ allowedDomains().join(', ') }}
            </p>
          }
        </app-grid-form-item>
        <app-grid-form-item [span]="1">
          <app-field-label
            label="Usuario"
            [required]="formMode() === 'create'"
            [tooltip]="loginAccessFieldTooltip()"
          />
          <lib-input
              label=""
              [readonly]="true"
              [disabled]="true"
              [value]="formMode() === 'create' ? formUsername() : formLoginUsernamePreview()"
              [placeholder]="formMode() === 'create' ? 'Se completa con el correo' : ''"
            />
        </app-grid-form-item>
        @if (formMode() === 'create' && !formSuperadmin()) {
          @if (isSuperAdmin()) {
            <app-grid-form-item [span]="1">
              <app-field-label label="Empresa" [required]="true" [tooltip]="createTenantFieldTooltip()" />
              <lib-select
                label=""
                [options]="createFormTenantOptions()"
                [value]="formCreateTenantId()"
                (valueChange)="onFormCreateTenantChange($event)"
              />
            </app-grid-form-item>
          }
          <app-grid-form-item [span]="isSuperAdmin() ? 1 : 'full'">
            <app-field-label label="Rol Vault" [required]="true" [tooltip]="createRoleFieldTooltip()" />
            <lib-select
              label=""
              [options]="roleOptions()"
              [value]="formCreateRole()"
              [disabled]="isSuperAdmin() && !formCreateTenantId()"
              (valueChange)="onFormCreateRoleChange($event)"
            />
          </app-grid-form-item>
        }
        <app-grid-form-item [span]="'full'" class="admin-users__options-row">
          <div class="admin-users__options-row-inner">
            <div class="admin-users__option-item">
              <app-field-label label="Usuario activo" [tooltip]="activeFieldTooltip()" />
              <lib-checkbox
                label=""
                [checked]="formActive()"
                (checkedChange)="formActive.set($event)"
              />
            </div>
            @if (isSuperAdmin() && formMode() === 'create') {
              <div class="admin-users__option-item">
                <app-field-label
                  label="Superadministrador del sistema"
                  [tooltip]="superadminFieldTooltip()"
                />
                <lib-checkbox
                  label=""
                  [checked]="formSuperadmin()"
                  [disabled]="hasSuperadminInList()"
                  (checkedChange)="formSuperadmin.set($event)"
                />
              </div>
            }
          </div>
        </app-grid-form-item>
        @if (formMode() === 'edit' && editingUser(); as u) {
          @if (!u.is_superadmin) {
            <app-grid-form-item [span]="showEditCompanyPicker() || canEditEditingUserRole() ? 1 : 'full'">
              <app-field-label
                label="Empresa"
                [required]="showEditCompanyPicker()"
                [tooltip]="editCompanyFieldTooltip()"
              />
              @if (showEditCompanyPicker()) {
                <lib-select
                  label=""
                  [options]="createFormTenantOptions()"
                  [value]="formEditTenantId()"
                  (valueChange)="onFormEditTenantChange($event)"
                />
              } @else {
                <p class="admin-users__hint admin-users__readonly-value">{{ editingUserCompanySummary() }}</p>
              }
            </app-grid-form-item>
            @if (canEditEditingUserRole() || showEditCompanyPicker()) {
              <app-grid-form-item [span]="1">
                <app-field-label label="Rol Vault" [required]="true" [tooltip]="editRoleFieldTooltip()" />
                <lib-select
                  label=""
                  [options]="roleOptions()"
                  [value]="formEditRole()"
                  [disabled]="showEditCompanyPicker() && !formEditTenantId()"
                  (valueChange)="onFormEditRoleChange($event)"
                />
              </app-grid-form-item>
            } @else {
              <app-grid-form-item [span]="1">
                <app-field-label label="Rol Vault" [tooltip]="editRoleFieldTooltip()" />
                <p class="admin-users__hint admin-users__readonly-value">
                  {{ tenantRoleLabel(formEditRole()) }}
                </p>
              </app-grid-form-item>
            }
          }
        }
      </app-grid-form>
      </form>
      }

      @if (formMode() === 'create' && formCreateStep() === 2) {
        <div
          class="admin-users__create-step2"
          role="group"
          aria-labelledby="admin-user-secret-label"
        >
          <dl class="admin-users__step2-summary">
            <div class="admin-users__step2-summary-row">
              <dt>Nombre para mostrar</dt>
              <dd>{{ formFullName() }}</dd>
            </div>
            <div class="admin-users__step2-summary-row">
              <dt>Correo electrónico</dt>
              <dd>{{ formEmail() }}</dd>
            </div>
            <div class="admin-users__step2-summary-row">
              <dt>Usuario de acceso</dt>
              <dd>{{ formLoginUsernamePreview() }}</dd>
            </div>
            @if (!formSuperadmin()) {
              <div class="admin-users__step2-summary-row">
                <dt>Rol Vault</dt>
                <dd>{{ tenantRoleLabel(formCreateRole()) }}</dd>
              </div>
            }
          </dl>
          <app-field-label
            id="admin-user-secret-label"
            label="Clave de acceso inicial"
            [required]="true"
            [tooltip]="createPasswordFieldTooltip()"
          />
          <app-admin-user-secret-field
            [fieldKey]="formAutofillNonce()"
            [resetToken]="secretFieldResetToken()"
            [reveal]="showFormPassword()"
            placeholder="Ingrese la clave de acceso"
            [ariaLabel]="'Clave de acceso para ' + formLoginUsernamePreview()"
            (valueChange)="formPassword.set($event)"
            (toggleReveal)="toggleFormPasswordVisibility()"
          />
          <app-admin-user-password-rules [password]="formPassword()" [policy]="passwordPolicy()" />
          <p class="admin-users__hint admin-users__hint--login">
            Para ingresar al sistema use el usuario de acceso
            <strong>{{ formLoginUsernamePreview() }}</strong>, no el correo completo.
          </p>
        </div>
      }

      @if (formMode() === 'edit') {
        <div
          class="admin-users__secret-panel"
          role="group"
          [attr.aria-labelledby]="'admin-user-edit-secret-label'"
        >
          <p class="admin-users__hint admin-users__hint--contract">
            Como administrador no necesita la clave anterior del usuario: el sistema la envía vacía
            (reset admin). Solo complete la nueva clave si desea cambiarla.
          </p>
          <div class="admin-users__password-reset-grid">
            <app-field-label
              id="admin-user-edit-current-password-label"
              label="Contraseña actual del usuario"
              tooltip="Reset admin: no se solicita ni se envía la clave anterior."
            />
            <div
              class="admin-users__password-reset-static"
              aria-labelledby="admin-user-edit-current-password-label"
            >
              <span class="admin-users__password-reset-static-value">No requerida (reset admin)</span>
            </div>
            <app-field-label
              id="admin-user-edit-secret-label"
              label="Nueva clave de acceso (opcional)"
              [tooltip]="passwordFieldTooltip()"
            />
            <app-admin-user-secret-field
              [fieldKey]="formAutofillNonce()"
              [resetToken]="secretFieldResetToken()"
              [reveal]="showFormPassword()"
              placeholder="Dejar vacío para no cambiar"
              ariaLabel="Nueva clave de acceso"
              (valueChange)="formPassword.set($event)"
              (toggleReveal)="toggleFormPasswordVisibility()"
            />
          </div>
          <app-admin-user-password-rules [password]="formPassword()" [policy]="passwordPolicy()" />
          <p class="admin-users__hint">
            Deje la nueva clave vacía para conservar la actual.
          </p>
        </div>
      }

      <footer class="admin-users__dialog-footer">
        @if (formMode() === 'edit' && editingUser(); as u) {
          @if (!u.is_superadmin && isSuperAdmin()) {
            <lib-button
              variant="secondary"
              type="button"
              prefixIcon="Shield"
              class="admin-users__footer-perms"
              [disabled]="saving()"
              (clicked)="openModulePermissionsFromEdit()"
            >
              Permisos de módulos
            </lib-button>
          }
        }
        @if (formMode() === 'create' && formCreateStep() === 2) {
          <lib-button variant="secondary" type="button" [disabled]="saving()" (clicked)="backCreateToDetailsStep()">
            Atrás
          </lib-button>
          <lib-button
            variant="primary"
            type="button"
            [loading]="saving()"
            [disabled]="saving() || !createPasswordReady()"
            (clicked)="saveUser()"
          >
            Crear
          </lib-button>
        } @else {
          <lib-button variant="secondary" type="button" [disabled]="saving()" (clicked)="closeForm()">Cancelar</lib-button>
          <lib-button
            variant="primary"
            type="button"
            [loading]="saving()"
            [disabled]="saving() || (formMode() === 'edit' && formPasswordBlocksSave())"
            (clicked)="saveUser()"
          >
            {{ formMode() === 'create' ? 'Siguiente' : 'Guardar cambios' }}
          </lib-button>
        }
      </footer>
    </div>
  </div>
}
